// assets/attendee-names.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} AttendeeNamesRefs
 * @property {HTMLElement} wrapper - The block wrapper, hidden below the minimum quantity.
 * @property {HTMLElement} fields - The container holding one fieldset per guest.
 */

/**
 * Renders a name and email field per ticket, following the product form's quantity.
 * Values are submitted as line item properties (`properties[Guest 1 name]`, ...) via the `form`
 * attribute. Below the configured minimum quantity the block is hidden and its inputs disabled,
 * so nothing is added to the cart line.
 * @extends Component<AttendeeNamesRefs>
 */
class AttendeeNamesComponent extends Component {
  requiredRefs = ['wrapper', 'fields'];

  /** @type {HTMLInputElement | null} */
  #quantityInput = null;

  #onQuantityUpdate = (/** @type {Event & {detail?: {quantity?: number, cartLine?: number}}} */ event) => {
    if (event.detail?.cartLine) return;
    const form = this.#quantityForm();
    if (form && event.target instanceof Node && !form.contains(event.target)) return;
    const quantity = Number(event.detail?.quantity);
    if (Number.isFinite(quantity)) this.#render(quantity);
  };

  #onQuantityInput = () => {
    this.#render(Number(this.#quantityInput?.value));
  };

  connectedCallback() {
    super.connectedCallback();

    const form = this.#quantityForm();
    const quantityInput = form?.elements.namedItem('quantity');
    this.#quantityInput = quantityInput instanceof HTMLInputElement ? quantityInput : null;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#quantityInput?.addEventListener('change', this.#onQuantityInput);

    if (this.#quantityInput) this.#render(Number(this.#quantityInput.value));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#quantityInput?.removeEventListener('change', this.#onQuantityInput);
  }

  /** @returns {HTMLFormElement | null} */
  #quantityForm() {
    const formId = this.dataset.formId ?? this.refs.wrapper?.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * Shows or hides the block and keeps one fieldset per additional guest (tickets 2..N; ticket 1
   * is the buyer's and uses the checkout name), preserving typed values.
   * @param {number} quantity
   */
  #render(quantity) {
    const { wrapper, fields } = this.refs;
    const settings = wrapper.dataset;
    const max = Number(settings.max) || 20;
    const minQuantity = Number(settings.minQuantity) || 2;
    const count = Math.floor(quantity) || 1;
    const visible = count >= minQuantity;

    wrapper.hidden = !visible;

    const lastIndex = Math.min(Math.max(count, 2), max);
    const current = Array.from(fields.querySelectorAll('[data-index]'));

    for (const guest of current.slice(lastIndex - 1)) guest.remove();

    const template = current[0];
    if (!template) return;

    for (let index = current.length + 2; index <= lastIndex; index++) {
      const clone = /** @type {HTMLElement} */ (template.cloneNode(true));
      clone.dataset.index = String(index);

      const legend = clone.querySelector('legend');
      if (legend) legend.textContent = legend.textContent?.replace(/\d+/, String(index)) ?? '';

      for (const input of clone.querySelectorAll('input')) {
        const labelTemplate = input.dataset.field === 'email' ? settings.emailLabel : settings.nameLabel;
        const label = (labelTemplate || `Guest [n] ${input.dataset.field}`).replace('[n]', String(index));
        const id = input.id.replace(/-\d+$/, `-${index}`);
        const labelElement = clone.querySelector(`label[for="${input.id}"]`);

        input.id = id;
        input.name = `properties[${label}]`;
        input.value = '';
        if (labelElement) {
          labelElement.setAttribute('for', id);
          labelElement.textContent = label;
        }
      }

      fields.append(clone);
    }

    for (const input of fields.querySelectorAll('input')) {
      input.disabled = !visible;
    }
  }
}

if (!customElements.get('attendee-names-component')) {
  customElements.define('attendee-names-component', AttendeeNamesComponent);
}
